import React, { useState, useEffect } from 'react';
import { localStorageManager, STORAGE_KEYS } from '../utils/localStorage';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type UserType = 'J' | 'H' | 'S';

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const [apiKey, setApiKey] = useState<string>('');
  const [user, setUser] = useState<UserType>('J');

  // 모달이 열릴 때 현재 값 로드
  useEffect(() => {
    if (isOpen) {
      // API Key 로드
      const storedApiKey = localStorageManager.getStringItem(STORAGE_KEYS.API_KEY);
      setApiKey(storedApiKey || '');
      
      // User 로드
      const storedUIState = localStorageManager.getItem<{ maxCardCount: number; user?: UserType }>(STORAGE_KEYS.UI_STATE);
      setUser(storedUIState?.user || 'J');
    }
  }, [isOpen]);

  const handleSave = () => {
    // API Key 저장
    if (apiKey.trim()) {
      localStorage.setItem(STORAGE_KEYS.API_KEY, apiKey.trim());
    } else {
      localStorage.removeItem(STORAGE_KEYS.API_KEY);
    }

    // User 저장 (UI State에 추가)
    const currentUIState = localStorageManager.getItem<{ maxCardCount: number; user?: UserType }>(STORAGE_KEYS.UI_STATE) || { maxCardCount: 50 };
    localStorageManager.setItem(STORAGE_KEYS.UI_STATE, {
      ...currentUIState,
      user,
    });

    // 커스텀 이벤트 발생 (같은 탭에서 변경사항 반영)
    window.dispatchEvent(new Event('localStorageChange'));

    onClose();
  };

  const handleCancel = () => {
    // 변경사항 취소하고 닫기
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-xl w-full max-w-md mx-4">
        {/* 헤더 */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">설정</h2>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 컨텐츠 */}
        <div className="px-6 py-4 space-y-6">
          {/* API Key */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              API Key
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="API Key를 입력하세요"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* User 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">
              User
            </label>
            <div className="flex space-x-4">
              {(['J', 'H', 'S'] as UserType[]).map((userOption) => (
                <label
                  key={userOption}
                  className="flex items-center cursor-pointer hover:bg-gray-700 px-4 py-2 rounded-md transition-colors"
                >
                  <input
                    type="radio"
                    name="user"
                    value={userOption}
                    checked={user === userOption}
                    onChange={() => setUser(userOption)}
                    className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="ml-2 text-gray-300">{userOption}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* 푸터 */}
        <div className="px-6 py-4 border-t border-gray-700 flex justify-end space-x-3">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-md transition-colors"
          >
            취소
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
          >
            저장
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
